import React, { useState } from 'react';

import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { useMutation, useQuery } from '@tanstack/react-query';
import ConfirmModal from 'src/components/shared/confirmModal/confirmModal';
import LoadingSpinnerWrapper from 'src/components/shared/loadingSpinner/loadingSpinnerWrapper';
import TopOptions from 'src/components/shared/topOptions/topOptions';
import {
  getModificationMutationOptions,
  getModificationQueryOptions,
} from 'src/services/modification/modificationQueries';
import { type Modification } from 'src/services/modification/types';

import JobModificationModal from './components/modificationModal';
import JobModificationTable from './components/modificationTable';

/**
 * Default component
 */
const JobModificationConfig = () => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] = useState(false);
  const [modificationToDelete, setModificationToDelete] = useState<Modification | null>(null);
  const [selectedModification, setSelectedModification] = useState<Modification | null>(null);
  const [searchField, setSearchField] = useState('');

  // Success toasts and table-data invalidation are handled globally by the
  // MutationCache (see index.tsx) via each mutation's `meta`.
  const tableDataQuery = useQuery(getModificationQueryOptions.taskModificationConfigTableData());
  const createOrUpdateMutation = useMutation(getModificationMutationOptions.createOrUpdate());
  const deleteMutation = useMutation(getModificationMutationOptions.delete());

  const onSave = async (payload: Modification) => {
    const { name } = await createOrUpdateMutation.mutateAsync(payload);
    setIsCreateModalOpen(false);
    setSearchField(name);
    setSelectedModification(null);
  };

  const deleteModification = async () => {
    await deleteMutation.mutateAsync(modificationToDelete!.id);
    setIsConfirmDeleteModalOpen(false);
    setModificationToDelete(null);
  };

  const onCancel = () => {
    setIsCreateModalOpen(false);
    setSelectedModification(null);
  };

  const onEditClick = (row: Modification) => {
    setSelectedModification(row);
    setIsCreateModalOpen(true);
  };

  const onDeleteClick = (row: Modification) => {
    setIsConfirmDeleteModalOpen(true);
    setModificationToDelete(row);
  };

  return (
    <div>
      <Row spacingInset="400 400 500 none">
        <Text alignment="center">Here you can view and modify the list of task modifications within RTW</Text>
      </Row>
      <LoadingSpinnerWrapper showSpinner={tableDataQuery.isPending}>
        <TopOptions
          label="Modification"
          onOpen={() => setIsCreateModalOpen(true)}
          searchField={searchField}
          setSearchField={setSearchField}
        />
        <JobModificationTable
          tableData={tableDataQuery.data ?? []}
          onDelete={onDeleteClick}
          onEdit={(row) => onEditClick(row!)}
          searchField={searchField}
        />
      </LoadingSpinnerWrapper>
      <JobModificationModal
        selectedModification={selectedModification}
        isOpen={isCreateModalOpen}
        onSave={onSave}
        onCancel={onCancel}
      />
      <ConfirmModal
        open={isConfirmDeleteModalOpen}
        setOpen={setIsConfirmDeleteModalOpen}
        onConfirm={() => deleteModification()}
        modalTitle="Confirm Delete?"
        modalDescription={`Are you sure you want to delete ${modificationToDelete?.name}?`}
      />
    </div>
  );
};

// **** Export **** //

export default JobModificationConfig;
